import { useSearchParams } from 'react-router-dom';
import { use11labsAgentCall } from './use11labsAgentCall';
import { useLivekitAgentCall } from './useLivekitAgentCall';
import {
  type BaseAgentCallProps,
  type AgentCallInterface,
  AgentProvider,
} from './types/agentCall.types';

/**
 * Factory hook that selects the appropriate agent call implementation
 * based on the 'engine' URL parameter.
 *
 * @param props - Agent call configuration
 * @returns AgentCallInterface implementation
 *
 * @example
 * // Use ElevenLabs (default)
 * // URL: /agent?product=prushield
 * const agent = useAgentCall({ productCode, callType });
 *
 * @example
 * // Use LiveKit
 * // URL: /agent?product=prushield&engine=livekit
 * const agent = useAgentCall({ productCode, callType });
 */
export const useAgentCall = (
  props: BaseAgentCallProps,
): AgentCallInterface => {
  const [searchParams] = useSearchParams();
  const engine = searchParams.get('engine')?.toLowerCase();

  // Determine provider from URL parameter
  const provider =
    engine === 'livekit' ? AgentProvider.LIVEKIT : AgentProvider.ELEVENLABS;

  switch (provider) {
    case AgentProvider.LIVEKIT:
      return useLivekitAgentCall(props);
    case AgentProvider.ELEVENLABS:
    default:
      return use11labsAgentCall(props);
  }
};

// Re-export types for convenience
export {
  CallState,
  LoadingState,
  AccentType,
  AgentProvider,
  type Transcript,
  type BaseAgentCallProps,
  type AgentCallInterface,
} from './types/agentCall.types';
